import type { BoxProps, FlexProps, ListItemProps } from '@chakra-ui/react';
import {
   Box,
   Flex,
   List,
   ListIcon,
   ListItem,
   ListProps,
   SimpleGrid,
   Text,
   TextProps,
   forwardRef,
} from '@chakra-ui/react';
import { useTranslations } from '@ifixit/i18n';
import { MenuItemType, type MenuItem, type Menu as MenuType } from '@ifixit/menu';
import { useTrackedOnClick } from '@ifixit/tracking-hooks';
import { AppStoreButtons } from '@ifixit/ui/components/app-store-buttons';
import type { ElementType, PropsWithChildren, ReactElement, ReactNode, SVGProps } from 'react';
import { connect } from 'react-redux';
import type { NewsletterFormProps } from './Newsletter';
import { NewsletterComponent } from './Newsletter';

export type NavSectionProps = {
   menu1: MenuType | null;
   menu2: MenuType | null;
   menu3: MenuType | null;
   newsletterForm: NewsletterFormProps | null;
   newsletterLinkMessageSubscribed: string;
   newsletterLinkMessageUnsubscribed: string;
   storeCode: string;
   updateConsentMessage: string;
   LinkComponent: ElementType;
   showAppStoreButtons: boolean;
};

export const NavigationSection = ({
   menu1,
   menu2,
   menu3,
   newsletterForm,
   newsletterLinkMessageSubscribed,
   newsletterLinkMessageUnsubscribed,
   storeCode,
   updateConsentMessage,
   LinkComponent,
   showAppStoreButtons,
}: NavSectionProps) => (
   <FooterTopSection>
      <FooterNavigationSection showAppStoreButtons={showAppStoreButtons}>
         <FooterNavigationList>
            <NavigationColumn menu={menu1} LinkComponent={LinkComponent} />
         </FooterNavigationList>
         <FooterNavigationList>
            <NavigationColumn menu={menu2} LinkComponent={LinkComponent} />
         </FooterNavigationList>
         <FooterNavigationList>
            <NavigationColumn
               menu={menu3}
               updateConsentMessage={updateConsentMessage}
               LinkComponent={LinkComponent}
            />
         </FooterNavigationList>
         {showAppStoreButtons && <NavigationAppStoreButtons LinkComponent={LinkComponent} />}
      </FooterNavigationSection>
      {newsletterForm && (
         <NewsletterComponent
            newsletterForm={newsletterForm}
            linkMessageSubscribed={newsletterLinkMessageSubscribed}
            linkMessageUnsubscribed={newsletterLinkMessageUnsubscribed}
            storeCode={storeCode}
         />
      )}
   </FooterTopSection>
);

export const NavigationColumn = ({
   menu,
   listFontSize = 'sm',
   updateConsentMessage,
   LinkComponent,
   ...otherProps
}: {
   menu: MenuType | null;
   listFontSize?: TextProps['fontSize'];
   updateConsentMessage?: string;
   LinkComponent: ElementType;
} & TextProps) => {
   if (!menu) {
      return null;
   }
   return (
      <>
         <NavigationTitle {...otherProps}>{menu.title}</NavigationTitle>
         <FooterNavigationListItems
            menu={menu}
            fontSize={listFontSize}
            updateConsentMessage={updateConsentMessage}
            LinkComponent={LinkComponent}
         />
      </>
   );
};

function NavigationTitle({ children, ...rest }: PropsWithChildren<TextProps>) {
   return (
      <Text fontSize="sm" fontWeight="semibold" color="white" mb="3" mt="0" {...rest}>
         {children}
      </Text>
   );
}

export function NavigationAppStoreButtons({
   LinkComponent,
   ...titleProps
}: { LinkComponent: ElementType } & TextProps) {
   const t = useTranslations('Footer');
   return (
      <FooterNavigationList>
         <NavigationTitle {...titleProps}>
            <UnstyledFooterNavigationLink
               href="/go/app"
               LinkComponent={LinkComponent}
               color="white"
               {...titleProps}
            >
               {t('downloadTheApp')}
            </UnstyledFooterNavigationLink>
         </NavigationTitle>
         <AppStoreButtons className="items-start sm:flex-col" buttonClassName="h-12" />
      </FooterNavigationList>
   );
}

const StyledFooterListItem = ({
   children,
   href,
   LinkComponent,
   ...props
}: { children: ReactNode; href?: string; LinkComponent?: ElementType } & TextProps) => (
   <FooterNavigationItem>
      <FooterNavigationLink
         LinkComponent={LinkComponent}
         fontSize="sm"
         fontWeight="normal"
         m={0}
         p={0}
         color="gray.300"
         _visited={{ color: 'gray.300' }}
         _hover={{ color: 'white', textDecoration: 'none' }}
         sx={{
            '&:visited:hover': {
               color: 'white',
            },
         }}
         href={href}
         {...props}
      >
         {children}
      </FooterNavigationLink>
   </FooterNavigationItem>
);

export const FooterNavigationListItems = ({
   menu,
   updateConsentMessage,
   LinkComponent,
   ...otherProps
}: { menu: MenuType; updateConsentMessage?: string; LinkComponent: ElementType } & TextProps) => {
   const listItems = menu.items.map((item: MenuItem, index: number) => {
      switch (item.type) {
         case MenuItemType.Link: {
            return (
               <StyledFooterListItem
                  key={index}
                  href={item.url}
                  LinkComponent={LinkComponent}
                  {...otherProps}
               >
                  {item.name}
               </StyledFooterListItem>
            );
         }
         default: {
            return null;
         }
      }
   });

   return (
      <>
         {listItems}
         {updateConsentMessage && (
            <UpdateConsentButton
               buttonText={updateConsentMessage}
               LinkComponent={LinkComponent}
               {...otherProps}
            />
         )}
      </>
   );
};

const stateToProps = (state: { consentBannerState: { bannerIsOpen: boolean } }) => ({
   bannerIsOpen: state.consentBannerState.bannerIsOpen,
});
const dispatchToProps = (
   dispatch: ({ type, value }: { type: string; value: unknown }) => void
) => ({
   setBannerIsOpen: (value: boolean) => dispatch({ type: 'TOGGLE_BANNER', value }),
   setDetailsIsOpen: (value: boolean) => dispatch({ type: 'TOGGLE_DETAILS', value }),
});

const UpdateConsentButton = connect(
   stateToProps,
   dispatchToProps
)(
   ({
      bannerIsOpen,
      setBannerIsOpen,
      setDetailsIsOpen,
      buttonText,
      LinkComponent,
      ...otherProps
   }: {
      bannerIsOpen: boolean;
      setBannerIsOpen: (val: boolean) => void;
      setDetailsIsOpen: (val: boolean) => void;
      buttonText: string;
      LinkComponent: ElementType;
   } & TextProps) => (
      <StyledFooterListItem
         data-testid="footer-cookie-consent"
         onClick={() => {
            if (!bannerIsOpen) {
               setBannerIsOpen(true);
            }
            setDetailsIsOpen(true);
         }}
         {...otherProps}
      >
         {buttonText}
      </StyledFooterListItem>
   )
);

export const FooterTopSection = forwardRef<FlexProps, 'div'>((props, ref) => (
   <Flex
      ref={ref}
      pb={10}
      flexDirection={{
         base: 'column',
         md: 'row',
      }}
      flexWrap="wrap"
      justifyContent="space-between"
      {...props}
   />
));

export function FooterNavigationSection({
   children,
   showAppStoreButtons,
}: PropsWithChildren<{ showAppStoreButtons: boolean }>) {
   return (
      <SimpleGrid
         columns={{
            base: 1,
            sm: showAppStoreButtons ? 4 : 3,
         }}
         spacing="4"
         p="0"
         mr={showAppStoreButtons ? 24 : undefined}
         autoFlow="row"
         flex="1"
      >
         {children}
      </SimpleGrid>
   );
}

export const FooterNavigationList = forwardRef<ListProps, 'ul'>(
   ({ children, ...otherProps }, ref) => (
      <List
         ref={ref}
         pb={{
            base: 4,
            sm: 0,
         }}
         m={{ base: 0 }}
         pl={0}
         {...otherProps}
      >
         {children}
      </List>
   )
);

export const FooterNavigationItem = (props: ListItemProps) => (
   <ListItem py="1" my="1" fontSize="sm" color="gray.300" {...props} />
);

type FooterNavigationLinkProps = BoxProps & {
   LinkComponent?: ElementType;
   icon?: (props: SVGProps<SVGSVGElement>) => ReactElement;
   href?: string;
};

export function FooterNavigationLink({
   LinkComponent,
   icon,
   href,
   children,
   ...otherProps
}: FooterNavigationLinkProps) {
   return (
      <UnstyledFooterNavigationLink
         LinkComponent={LinkComponent}
         icon={icon}
         href={href}
         cursor="pointer"
         transition="all 400ms"
         fontWeight="normal"
         m={0}
         p={0}
         _hover={{
            color: 'white',
            textDecoration: 'none',
         }}
         {...otherProps}
      >
         {children}
      </UnstyledFooterNavigationLink>
   );
}

const UnstyledFooterNavigationLink = forwardRef<FooterNavigationLinkProps, 'a'>(
   ({ LinkComponent, icon, children, ...otherProps }, ref) => {
      const trackedOnClick = useTrackedOnClick({
         clickName: children?.toString(),
         ...otherProps,
      });
      return (
         <Box ref={ref} as={LinkComponent ?? 'a'} onClick={trackedOnClick} {...otherProps}>
            {icon && <ListIcon as={icon} boxSize="4" filter="opacity(0.5)" />}
            {children}
         </Box>
      );
   }
);
